import { STORE_ORIGIN } from '../config';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import {
  FiArrowLeft,
  FiCheckCircle,
  FiShoppingCart,
  FiAlertCircle,
  FiCreditCard,
  FiXCircle
} from 'react-icons/fi';

const API_BASE_URL = `${STORE_ORIGIN}/backend/`;

function getCheckoutRequestId(cart, form) {
  const fingerprint = JSON.stringify([cart.map(i => [i.product_id, i.variation_id, i.quantity]), form]);
  let previous;
  try { previous = JSON.parse(sessionStorage.getItem('checkout_request')); } catch {}
  if (previous?.fingerprint === fingerprint) return previous.id;
  const request = { fingerprint, id: crypto.randomUUID() };
  sessionStorage.setItem('checkout_request', JSON.stringify(request));
  return request.id;
}

const CheckoutPage = () => {
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();

  /* =========================
     STATE
  ========================= */
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [isCartEmpty, setIsCartEmpty] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [isCheckingPending, setIsCheckingPending] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    notes: '',
    payment_method: 'cod'
  });

  const [errors, setErrors] = useState({});
  const [shippingCost, setShippingCost] = useState(0);
  const [qatarCities, setQatarCities] = useState([]);
  const [sadadEnabled, setSadadEnabled] = useState(false);
  const [storeReady, setStoreReady] = useState(false);
  const [confirmedTotal, setConfirmedTotal] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    fetch(API_BASE_URL + 'store_config.php', { signal: controller.signal }).then(r => r.json()).then(data => {
      if (!data.success) throw new Error(data.message);
      setQatarCities(data.cities); setSadadEnabled(data.sadad_enabled); setStoreReady(true);
    }).catch(error => { if (error.name !== 'AbortError') setApiError('Unable to load delivery options. Please refresh checkout.'); });
    return () => controller.abort();
  }, []);

  /* =========================
     STATIC DATA
  ========================= */


  const cartSubtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const total = cartSubtotal + shippingCost;

  /* =========================
     UTILITY FUNCTIONS
  ========================= */
  
  // Generate or retrieve device/session ID
  const getDeviceId = useCallback(() => {
    let deviceId = localStorage.getItem('checkout_device_id');
    if (!deviceId) {
      // Generate a unique device ID
      deviceId = 'dev_' + crypto.randomUUID();
      localStorage.setItem('checkout_device_id', deviceId);
    }
    return deviceId;
  }, []);

  // Generate or retrieve session token
  const getSessionToken = useCallback(() => {
    let sessionToken = sessionStorage.getItem('checkout_session_token');
    if (!sessionToken) {
      sessionToken = 'sess_' + crypto.randomUUID();
      sessionStorage.setItem('checkout_session_token', sessionToken);
    }
    return sessionToken;
  }, []);

  /* =========================
     EFFECTS
  ========================= */
  useEffect(() => {
    setIsCartEmpty(cart.length === 0);
  }, [cart]);

  useEffect(() => {
    if (!formData.city) {
      setShippingCost(0);
      return;
    }
    const city = qatarCities.find(c => c.name === formData.city);
    setShippingCost(city ? city.shippingCost : 0);
  }, [formData.city, qatarCities]);

  // Check for pending orders for THIS DEVICE/SESSION only
  useEffect(() => {
    const checkPendingOrder = async () => {
      // Get device/session identifiers
      const deviceId = getDeviceId();
      const sessionToken = getSessionToken();

      setIsCheckingPending(true);
      
      try {
        const response = await fetch(`${API_BASE_URL}check_pending_orders.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            device_id: deviceId,
            session_token: sessionToken,
            email: formData.email.trim() || null,
            phone: formData.phone.trim() || null
          })
        });

        const data = await response.json();
        
        if (data.success && data.has_pending) {
          // Only show pending order if it belongs to this device/session
          setPendingOrder({
            order_number: data.order_number,
            email: data.email,
            phone: data.phone,
            amount: data.amount,
            created_at: data.created_at,
            device_id: data.device_id,
            session_token: data.session_token
          });
        } else {
          setPendingOrder(null);
        }
      } catch (error) {
        console.error('Error checking pending order:', error);
        setPendingOrder(null);
      } finally {
        setIsCheckingPending(false);
      }
    };

    // Debounce the check to avoid too many API calls
    const timer = setTimeout(checkPendingOrder, 800);
    return () => clearTimeout(timer);
  }, [formData.email, formData.phone, getDeviceId, getSessionToken]);

  /* =========================
     HANDLERS
  ========================= */
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (apiError) setApiError(null);
  }, [errors, apiError]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    const requiredFields = ['first_name', 'last_name', 'email', 'phone', 'address', 'city'];

    requiredFields.forEach(field => {
      if (!formData[field]?.trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Invalid email address';
    }

    if (formData.phone && !/^\+?[\d\s-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Invalid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleCancelPendingOrder = useCallback(async () => {
    if (!pendingOrder) return;

    try {
      const response = await fetch(`${API_BASE_URL}cancel_pending_order.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_number: pendingOrder.order_number,
          device_id: pendingOrder.device_id,
          session_token: pendingOrder.session_token,
          email: pendingOrder.email,
          phone: pendingOrder.phone
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setPendingOrder(null);
        setApiError(null);
        // Clear stored identifiers if this was the only pending order
        // Optionally generate new session token
        sessionStorage.removeItem('checkout_session_token');
        sessionStorage.removeItem('checkout_request');
        // Show success message
        setApiError('Order cancelled successfully. You can now place a new order.');
        setTimeout(() => setApiError(null), 3000);
      } else {
        setApiError(data.message || 'Failed to cancel order. Please try again.');
      }
    } catch (error) {
      setApiError('Network error. Please try again.');
    }
  }, [pendingOrder]);

  /* =========================
     SUBMIT HANDLER
  ========================= */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Block only if there's a pending SADAD order for THIS DEVICE
    if (pendingOrder) {
      setApiError(
        `You have a pending SADAD payment for order #${pendingOrder.order_number} from this device. ` +
        `Please complete or cancel this payment before creating a new order.`
      );
      return;
    }
    
    if (!storeReady || isProcessingPayment || !cart.length || !validateForm()) return;

    setIsProcessingPayment(true);
    setApiError(null);

    try {
      // Get device/session identifiers
      const deviceId = getDeviceId();
      const sessionToken = getSessionToken();

      /* ---------- CREATE ORDER ---------- */
      const orderResponse = await fetch(`${API_BASE_URL}orders.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          idempotency_key: getCheckoutRequestId(cart, formData),
          device_id: deviceId,
          session_token: sessionToken,
          subtotal: cartSubtotal.toFixed(2),
          shipping_cost: shippingCost.toFixed(2),
          total: total.toFixed(2),
          items: cart
        })
      });

      const orderResult = await orderResponse.json();
      if (!orderResult.success) throw new Error(orderResult.message);

      const newOrderNumber = orderResult.order_number;
      setConfirmedTotal(Number(orderResult.total));
      sessionStorage.setItem('order_token_' + newOrderNumber, sessionToken);
      if (orderResult.order_status === 'cancelled') throw new Error('This order was cancelled. Please start a new checkout.');

      /* ---------- COD PAYMENT ---------- */
      if (formData.payment_method === 'cod') {
        setOrderNumber(newOrderNumber);
        clearCart();
        sessionStorage.removeItem('checkout_request');
        setOrderSuccess(true);
        setIsProcessingPayment(false);
        
        // Clear session data after successful COD order
        sessionStorage.removeItem('checkout_session_token');
        localStorage.removeItem('checkout_device_id');
        return;
      }

      /* ---------- SADAD PAYMENT ---------- */
      // Initiate SADAD payment
      const sadadResponse = await fetch(`${API_BASE_URL}initiate_sadad.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_number: newOrderNumber,
          customer_email: formData.email,
          customer_phone: formData.phone,
          customer_name: `${formData.first_name} ${formData.last_name}`,
          device_id: deviceId,
          session_token: sessionToken
        })
      });

      const sadadResult = await sadadResponse.json();
      if (!sadadResult.success || !sadadResult.payment_url) {
        throw new Error('Failed to initiate payment. Please try again or choose COD.');
      }

      // Store the pending order info before redirect
      // This helps when user returns from payment gateway
      sessionStorage.setItem('pending_order_number', newOrderNumber);
      sessionStorage.setItem('pending_order_device', deviceId);

      // Redirect to SADAD payment page
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = sadadResult.payment_url;

      Object.entries(sadadResult.payment_data || {}).forEach(([key, val]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = val;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();

    } catch (err) {
      setApiError(err.message || 'Order failed. Please try again.');
      if (formData.payment_method === 'sadad') {
        try {
          const response = await fetch(API_BASE_URL + 'check_pending_orders.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ device_id: getDeviceId(), session_token: getSessionToken() }) });
          const pending = await response.json();
          if (pending.success && pending.has_pending) setPendingOrder(pending);
        } catch {}
      }
      setIsProcessingPayment(false);
    }
  }, [
    validateForm,
    isProcessingPayment,
    storeReady,
    pendingOrder,
    formData,
    cart,
    cartSubtotal,
    shippingCost,
    total,
    clearCart,
    getDeviceId,
    getSessionToken
  ]);

  /* =========================
     UI COMPONENTS
  ========================= */

  // Success screen (unchanged)
  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <FiCheckCircle className="text-green-500 text-6xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Order Placed Successfully</h2>
          <p className="mb-4">Order Number: <b>{orderNumber}</b><br />Total: QAR {(confirmedTotal ?? total).toFixed(2)}</p>
          <button
            onClick={() => {
              // Clear any remaining session data
              sessionStorage.removeItem('checkout_session_token');
              sessionStorage.removeItem('pending_order_number');
              sessionStorage.removeItem('pending_order_device');
              navigate('/');
            }}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  // Empty cart (unchanged)
  if (isCartEmpty) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiShoppingCart className="text-6xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-600">Your cart is empty</h3>
          <button
            onClick={() => navigate('/')}
            className="mt-4 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg"
          >
            Start Shopping
          </button>
        </div>
      </div>
    );
  }

  // Rest of the JSX remains the same as your original code
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Back button */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center text-green-600 hover:text-green-700 mb-6"
        >
          <FiArrowLeft className="mr-2" /> Back to Cart
        </button>

        {/* Pending Order Warning - Only shows if from this device */}
        {pendingOrder && (
          <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
            <div className="flex items-start">
              <FiAlertCircle className="text-yellow-500 text-xl mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-yellow-800 mb-1">
                      Pending SADAD Payment (This Device)
                    </h3>
                    <p className="text-yellow-700 text-sm mb-2">
                      You have an unpaid SADAD payment for order <strong>#{pendingOrder.order_number}</strong> from this device.
                    </p>
                    <div className="text-xs text-yellow-600 space-y-1">
                      <p>Amount: QAR {pendingOrder.amount}</p>
                      {pendingOrder.created_at && (
                        <p>Created: {new Date(pendingOrder.created_at).toLocaleDateString()} at {new Date(pendingOrder.created_at).toLocaleTimeString()}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleCancelPendingOrder}
                    className="ml-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md flex items-center whitespace-nowrap"
                  >
                    <FiXCircle className="mr-2" />
                    Cancel Order
                  </button>
                </div>
                <p className="mt-3 text-sm text-red-600 font-medium">
                  Please cancel this pending order to place a new one from this device.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {isCheckingPending && (
          <div className="mb-6 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-sm text-blue-600">Checking for pending payments...</span>
            </div>
          </div>
        )}

        {/* API Error Display */}
        {apiError && (
          <div className={`mb-6 p-4 rounded-lg border-l-4 ${
            apiError.includes('cancelled successfully') 
              ? 'bg-green-50 border-green-500 text-green-700'
              : 'bg-red-50 border-red-500 text-red-700'
          }`}>
            {apiError}
          </div>
        )}

        {/* Main Checkout Form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column: Customer Information */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Customer Information</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name*</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    disabled={!storeReady || isProcessingPayment || Boolean(pendingOrder)}
                    className={`w-full px-3 py-2 border ${errors.first_name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-green-500 disabled:bg-gray-100`}
                  />
                  {errors.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name*</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    disabled={!storeReady || isProcessingPayment || Boolean(pendingOrder)}
                    className={`w-full px-3 py-2 border ${errors.last_name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-green-500 disabled:bg-gray-100`}
                  />
                  {errors.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={!storeReady || isProcessingPayment || Boolean(pendingOrder)}
                  className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-green-500 disabled:bg-gray-100`}
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone*</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+974 XXXXXXXX"
                  disabled={!storeReady || isProcessingPayment || Boolean(pendingOrder)}
                  className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-green-500 disabled:bg-gray-100`}
                />
                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address*</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={!storeReady || isProcessingPayment || Boolean(pendingOrder)}
                  className={`w-full px-3 py-2 border ${errors.address ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-green-500 disabled:bg-gray-100`}
                />
                {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">City*</label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  disabled={!storeReady || isProcessingPayment || Boolean(pendingOrder)}
                  className={`w-full px-3 py-2 border ${errors.city ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-green-500 disabled:bg-gray-100`}
                >
                  <option value="">Select city</option>
                  {qatarCities.map(city => (
                    <option key={city.name} value={city.name}>
                      {city.name} (QAR {city.shippingCost})
                    </option>
                  ))}
                </select>
                {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  disabled={!storeReady || isProcessingPayment || Boolean(pendingOrder)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                <div className="space-y-3">
                  <label className={`flex items-start p-3 border rounded-md cursor-pointer transition-colors ${formData.payment_method === 'cod' ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:bg-gray-50'} ${isProcessingPayment || pendingOrder ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="cod"
                      checked={formData.payment_method === 'cod'}
                      onChange={handleInputChange}
                      disabled={!storeReady || isProcessingPayment || Boolean(pendingOrder)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <span className="font-medium flex items-center">
                        <FiCreditCard className="mr-2" />
                        Cash on Delivery
                      </span>
                      <p className="text-sm text-gray-600 mt-1">Pay when you receive your order</p>
                    </div>
                  </label>

                  {sadadEnabled && <label className={`flex items-start p-3 border rounded-md cursor-pointer transition-colors ${formData.payment_method === 'sadad' ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:bg-gray-50'} ${isProcessingPayment || pendingOrder ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="sadad"
                      checked={formData.payment_method === 'sadad'}
                      onChange={handleInputChange}
                      disabled={!storeReady || isProcessingPayment || Boolean(pendingOrder)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <span className="font-medium flex items-center">
                        <FiCreditCard className="mr-2" />
                        SADAD Online Payment
                      </span>
                      <p className="text-sm text-gray-600 mt-1">Pay securely with SADAD payment gateway</p>
                    </div>
                  </label>}
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!storeReady || isProcessingPayment || Boolean(pendingOrder)}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-3 rounded-lg flex justify-center items-center transition-colors"
              >
                {isProcessingPayment ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {formData.payment_method === 'cod' ? 'Placing Order...' : 'Redirecting to Payment...'}
                  </>
                ) : pendingOrder ? (
                  'Checkout Blocked - Cancel Pending Order First'
                ) : (
                  formData.payment_method === 'cod' ? 'Place COD Order' : 'Proceed to SADAD Payment'
                )}
              </button>
            </form>
          </div>

          {/* Right Column: Order Summary */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Order Summary</h2>
            
            <div className="mb-6 max-h-96 overflow-y-auto">
              {cart.map(item => (
                <div key={item.id} className="flex items-center py-3 border-b">
                  {item.image && (
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded mr-3" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{item.name}</p>
                    <div className="text-sm text-gray-500 mt-1">
                      <span>Qty: {item.quantity}</span>
                      {item.size && <span className="ml-2">Size: {item.size}</span>}
                    </div>
                  </div>
                  <p className="font-medium">QAR {(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
            
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>QAR {cartSubtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping:</span>
                <span>QAR {shippingCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>Total:</span>
                <span className="text-green-600">QAR {total.toFixed(2)}</span>
              </div>
            </div>
            
            {formData.payment_method === 'cod' && (
              <div className="mt-6 p-4 bg-green-50 rounded-md">
                <p className="text-sm text-green-700">
                  <FiAlertCircle className="inline mr-1" />
                  No payment required now. Pay QAR {total.toFixed(2)} upon delivery.
                </p>
              </div>
            )}
            
            {formData.payment_method === 'sadad' && (
              <div className="mt-6 p-4 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-700">
                  <FiCreditCard className="inline mr-1" />
                  You will be redirected to SADAD payment gateway to complete your payment of QAR {total.toFixed(2)}.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
